import { useEffect, useMemo, useState } from "react";
import {
  Document,
  Page,
  pdf as renderPdf,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { useComfort } from "../ComfortContext";
import { fill, getDictionary, type Locale } from "../../i18n/dictionary";
import { gameName } from "./ChildDetail";
import {
  buildExportModel,
  GUARDRAIL_LINE,
  type ExportModel,
} from "../../domain/pdfModel";
import { toCsv } from "../../domain/exportData";
import { canViewerSee } from "../../domain/consent";
import type { ConsentState } from "../../domain/consent";
import type { ConsentScopeKey } from "../../domain/types";
import { useOutletContext } from "react-router-dom";
import { useLinkedChildren } from "./useLinkedChildren";
import { LoadingChildren, ChildrenLoadError } from "./LoadState";
import { FIXTURE_ACCOUNT_ID, type LinkedChild } from "./data";

// ---- PDF document -----------------------------------------------------------

// CSS vars are not available in @react-pdf/renderer — use static values.
const pdf = StyleSheet.create({
  page: { padding: 44, fontSize: 11, fontFamily: "Helvetica", color: "#111827" },
  title: { fontSize: 22, fontFamily: "Helvetica-Bold", marginBottom: 4 },
  childLabel: { fontSize: 13, color: "#374151", marginBottom: 4 },
  generatedAt: { fontSize: 9, color: "#9CA3AF", marginBottom: 28 },
  sectionLabel: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 1,
    color: "#9CA3AF",
    marginBottom: 7,
    marginTop: 20,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 7,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
    borderBottomStyle: "solid",
  },
  domain: { flex: 1, fontSize: 11, color: "#111827" },
  wellBadge: { fontSize: 9, fontFamily: "Helvetica-Bold", color: "#065F46" },
  exploreBadge: { fontSize: 9, fontFamily: "Helvetica-Bold", color: "#92400E" },
  gameLabel: { flex: 1, fontSize: 11, color: "#111827" },
  sessionMeta: { fontSize: 10, color: "#6B7280", marginLeft: 10 },
  guardrailWrap: {
    marginTop: 32,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    borderTopStyle: "solid",
    paddingTop: 12,
  },
  guardrailText: { fontSize: 9, color: "#9CA3AF", lineHeight: 1.5 },
  noData: { fontSize: 10, color: "#6B7280", marginTop: 4 },
  line: { fontSize: 11, color: "#111827", marginBottom: 3 },
  stepRow: { paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: "#F3F4F6", borderBottomStyle: "solid" },
  stepTitle: { fontSize: 11, fontFamily: "Helvetica-Bold", color: "#111827" },
  stepText: { fontSize: 10, color: "#374151", marginTop: 1 },
  sessionMetaBlock: { fontSize: 9, color: "#6B7280", marginTop: 1 },
});

function shortDate(iso: string, locale: Locale): string {
  return new Date(iso).toLocaleDateString(locale === "ms" ? "ms-MY" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** The PDF section headings, from the same dictionary the screen uses. */
function pdfHeadings(locale: Locale) {
  const d = getDictionary(locale).service;
  return {
    onePager: d.progress.onePagerTitle,
    support: d.progress.supportTitle,
    practice: d.progress.practiceTitle,
    newSince: d.progress.newTitle,
    newNone: d.progress.newNone,
    working: d.progress.workingTitle,
    workingNone: d.progress.workingNone,
    areas: d.progress.areasTitle,
    areasNote: d.progress.areasNote,
    areaLine: d.progress.areaAria,
    recent: d.childDetail.recentPlay,
    noSessions: d.childDetail.noSessions,
    shared: d.progress.sharedWithFamily,
  };
}

function buildPdfDoc(model: ExportModel, locale: Locale) {
  const now = shortDate(new Date().toISOString(), locale);
  const h = pdfHeadings(locale);
  return (
    <Document title={model.header.title}>
      <Page size="A4" style={pdf.page}>
        <Text style={pdf.title}>{model.header.title}</Text>
        <Text style={pdf.childLabel}>{model.header.childLabel}</Text>
        <Text style={pdf.generatedAt}>
          {h.onePager} · {model.period} · {h.shared} · {now}
        </Text>

        <Text style={pdf.sectionLabel}>{h.support.toUpperCase()}</Text>
        {model.support.map((l, i) => (
          <Text key={i} style={pdf.line}>{l.text}</Text>
        ))}

        <Text style={pdf.sectionLabel}>{h.practice.toUpperCase()}</Text>
        {model.practice.map((l, i) => (
          <Text key={i} style={pdf.line}>{l.text}</Text>
        ))}

        <Text style={pdf.sectionLabel}>{h.newSince.toUpperCase()}</Text>
        {model.newSince.length > 0 ? (
          model.newSince.map((s, i) => (
            <View key={i} style={pdf.stepRow}>
              <Text style={pdf.stepTitle}>{s.step}: {s.stage}</Text>
              <Text style={pdf.stepText}>{s.canDo}</Text>
              <Text style={pdf.sessionMetaBlock}>{s.reached} · {s.lastPlayed}</Text>
            </View>
          ))
        ) : (
          <Text style={pdf.noData}>{h.newNone}</Text>
        )}

        <Text style={pdf.sectionLabel}>{h.working.toUpperCase()}</Text>
        {model.workingOn.length > 0 ? (
          model.workingOn.map((s, i) => (
            <View key={i} style={pdf.stepRow}>
              <Text style={pdf.stepTitle}>{s.step}</Text>
              <Text style={pdf.stepText}>{s.canDo}</Text>
              <Text style={pdf.sessionMetaBlock}>{[s.since, s.lastPlayed].filter(Boolean).join(" · ")}</Text>
            </View>
          ))
        ) : (
          <Text style={pdf.noData}>{h.workingNone}</Text>
        )}

        {model.areas.length > 0 && (
          <>
            <Text style={pdf.sectionLabel}>{h.areas.toUpperCase()}</Text>
            {model.areas.map((a, i) => (
              // A sentence, not an arrow: the built-in PDF font has no "→" glyph.
              <Text key={i} style={pdf.line}>
                {fill(h.areaLine, { area: a.area, start: a.start, end: a.end })}
              </Text>
            ))}
            <Text style={pdf.noData}>{h.areasNote}</Text>
          </>
        )}

        <Text style={pdf.sectionLabel}>{h.recent.toUpperCase()}</Text>
        {model.sessions.length > 0 ? (
          model.sessions.slice(0, 10).map((s, i) => (
            <View key={i} style={pdf.row}>
              <Text style={pdf.gameLabel}>{gameName(s.gameId, locale)}</Text>
              <Text style={pdf.sessionMeta}>{s.duration}</Text>
              <Text style={pdf.sessionMeta}>{shortDate(s.at, locale)}</Text>
            </View>
          ))
        ) : (
          <Text style={pdf.noData}>{h.noSessions}</Text>
        )}

        {/* Footer + guardrail — always present */}
        <View style={pdf.guardrailWrap}>
          <Text style={pdf.guardrailText}>{model.footer}</Text>
          <Text style={pdf.guardrailText}>{model.guardrail}</Text>
        </View>
      </Page>
    </Document>
  );
}

// ---- Download helpers -------------------------------------------------------

function downloadBlob(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---- UI sub-components ------------------------------------------------------

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: ".12em",
        textTransform: "uppercase",
        color: "var(--ink-faint)",
        margin: "0 0 12px",
      }}
    >
      {children}
    </h2>
  );
}

function ChildPickerCard({
  lc,
  selected,
  ownerTagLabel,
  viewerTagLabel,
  onSelect,
}: {
  lc: LinkedChild;
  selected: boolean;
  ownerTagLabel: string;
  viewerTagLabel: string;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 18px",
        background: selected ? "var(--brand-tint)" : "var(--surface)",
        border: `1.5px solid ${selected ? "var(--brand)" : "var(--line)"}`,
        borderRadius: "var(--r-lg)",
        cursor: "pointer",
        textAlign: "left",
        minHeight: 64,
        fontFamily: "var(--font-body)",
        width: "100%",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: selected ? "var(--brand)" : "var(--brand-tint)",
          display: "grid",
          placeItems: "center",
          flex: "none",
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 16,
          color: selected ? "#fff" : "var(--brand)",
        }}
      >
        {lc.child.displayLabel.charAt(0)}
      </span>
      <div>
        <b
          style={{
            display: "block",
            fontSize: 15.5,
            fontWeight: 600,
            color: "var(--ink-deep)",
            fontFamily: "var(--font-body)",
          }}
        >
          {lc.child.displayLabel}
        </b>
        <span style={{ fontSize: 13, color: "var(--ink-faint)" }}>
          {lc.link.relation === "owner" ? ownerTagLabel : viewerTagLabel}
        </span>
      </div>
      {selected && (
        <span
          aria-hidden="true"
          style={{ marginLeft: "auto", color: "var(--brand)", fontWeight: 700, fontSize: 18 }}
        >
          ✓
        </span>
      )}
    </button>
  );
}

// ---- Main component ---------------------------------------------------------

/**
 * The consent facts for ONE adult's view of ONE child, built from the data the
 * linked-children hook already returned.
 *
 * Deliberately narrow: it carries the requesting adult's own grant and nothing
 * else. A caregiver never needs to know what OTHER viewers were granted in order
 * to export their own copy, and the export screen must not become a place where
 * one viewer can enumerate another.
 */
export function consentStateFor(lc: LinkedChild): ConsentState {
  const grants: Record<string, readonly ConsentScopeKey[]> = {};
  if (
    lc.link.relation === "viewer" &&
    lc.consent &&
    !lc.consent.revokedAt &&
    lc.link.scope.length > 0
  ) {
    grants[lc.link.accountId] = lc.link.scope;
  }
  return {
    childId: lc.child.id,
    ownerAccountId: lc.child.ownerAccountId,
    grants,
  };
}

export function ProgressExport() {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const t = dict.service.export;
  const th = dict.service.home;
  const tch = dict.service.children;

  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  const outlet = useOutletContext<{ userId?: string } | null>();
  const accountId = outlet?.userId ?? FIXTURE_ACCOUNT_ID;
  const { linked, loading, error, refresh } = useLinkedChildren(outlet?.userId);
  const activeChildren = useMemo(
    () => linked.filter((lc) => lc.linkState === "active"),
    [linked],
  );

  const selectedLC = useMemo(
    () => activeChildren.find((lc) => lc.child.id === selectedChildId) ?? null,
    [activeChildren, selectedChildId],
  );

  // Consent check: owner holds export scope implicitly; viewer needs it granted.
  //
  // This USED to call buildConsentState(childId), which looks the child up in
  // src/domain/fixtures. Against a live UUID that lookup misses, so the state
  // came back with ownerAccountId "unknown" and no grants — meaning canExport
  // was false for EVERY live user, including a child's own owner. Nobody on the
  // deployed site could export anything.
  //
  // The linked-children hook already carries everything the gate needs for the
  // requesting adult, in both fixtures and live mode, so derive it from there.
  // Memoised: a fresh object every render made exportModel (and so the PDF
  // pre-build effect below, which sets state) run on every render — a loop.
  const consentState = useMemo(
    () => (selectedLC ? consentStateFor(selectedLC) : null),
    [selectedLC],
  );
  const canExport =
    consentState !== null && canViewerSee(consentState, accountId, "export");

  // Rebuilt whenever the selection, the consent decision or the identity of the
  // requester changes. The previous dependency list was `[selectedChildId]` with
  // the exhaustive-deps rule silenced, so the model could be built from a
  // previous child's sessions after the data finished loading.
  const exportModel = useMemo(() => {
    if (!selectedLC || !consentState || !canExport) return null;
    try {
      return buildExportModel(
        selectedLC.child,
        {
          sessions: selectedLC.sessions,
          stepStates: selectedLC.stepStates,
          supportProfile: selectedLC.supportProfile,
        },
        // Wide range: the caller has already been scoped by consent, and the
        // range only bounds which sessions appear in the file. The One-Pager
        // sections cover the last 13 weeks.
        { from: "2020-01-01T00:00:00.000Z", to: new Date().toISOString() },
        { state: consentState, requestedBy: accountId },
        { locale },
      );
    } catch {
      return null;
    }
  }, [selectedLC, consentState, canExport, accountId, locale]);

  const fileSlug = selectedLC
    ? selectedLC.child.displayLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-")
    : "progress";

  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  function handleDownloadJson() {
    if (!exportModel) return;
    downloadBlob(
      JSON.stringify(exportModel, null, 2),
      `${fileSlug}-export.json`,
      "application/json",
    );
  }

  function handleDownloadCsv() {
    if (!exportModel) return;
    const guardrailRow = `"note","${exportModel.guardrail.replace(/"/g, '""')}"`;
    const footerRow = `"note","${exportModel.footer.replace(/"/g, '""')}"`;
    const sections = [
      exportModel.support,
      exportModel.practice,
      exportModel.newSince,
      exportModel.workingOn,
      exportModel.areas,
      exportModel.sessions,
    ]
      .filter((rows) => rows.length > 0)
      .map((rows) => toCsv(rows as unknown as Record<string, string | number>[]));
    const content = [guardrailRow, footerRow, "", ...sections.flatMap((c) => [c, ""])].join("\r\n");
    downloadBlob(content, `${fileSlug}-export.csv`, "text/csv");
  }

  // PDF is generated imperatively on click (pdf().toBlob()) rather than by
  // rendering <PDFDownloadLink> into the tree: react-pdf's declarative
  // components use host React hooks that crash under React 19, whereas the
  // imperative renderer uses react-pdf's own reconciler. try/catch means a PDF
  // failure can never take the screen (or the app) down — JSON/CSV still work.
  // The PDF is built AHEAD of the tap: navigator.share() must be called inside
  // the short user-activation window of the click, and building a PDF can take
  // longer than that. Downloading reuses the same ready blob.
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  useEffect(() => {
    setPdfBlob(null);
    if (!exportModel) return;
    let alive = true;
    // Only in a real browser: jsdom (tests) has no layout engine for react-pdf.
    if (typeof navigator === "undefined" || /jsdom/i.test(navigator.userAgent)) return;
    renderPdf(buildPdfDoc(exportModel, locale))
      .toBlob()
      .then((b) => {
        if (alive) setPdfBlob(b);
      })
      .catch(() => {
        // The download path builds it again on click and reports a failure there.
      });
    return () => {
      alive = false;
    };
  }, [exportModel, locale]);

  const pdfFileName = `${fileSlug}-support-one-pager.pdf`;
  const canShareFiles =
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    typeof File === "function";

  async function handleShare() {
    if (!exportModel || pdfBusy) return;
    if (!pdfBlob) {
      // Not ready yet (or no share support): fall back to the download.
      await handleDownloadPdf();
      return;
    }
    const file = new File([pdfBlob], pdfFileName, { type: "application/pdf" });
    if (!navigator.canShare?.({ files: [file] })) {
      await handleDownloadPdf();
      return;
    }
    try {
      await navigator.share({ files: [file], title: t.shareTitle, text: t.shareText });
    } catch (err) {
      // The person closing the share sheet is not an error.
      if (err instanceof DOMException && err.name === "AbortError") return;
      await handleDownloadPdf();
    }
  }

  async function handleDownloadPdf() {
    if (!exportModel || pdfBusy) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      const blob = pdfBlob ?? (await renderPdf(buildPdfDoc(exportModel, locale)).toBlob());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdfFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setPdfError(
        "Couldn't build the PDF just now — the JSON or CSV export below still works.",
      );
    } finally {
      setPdfBusy(false);
    }
  }

  // Placed AFTER every hook (rules of hooks), but before any UI that would
  // otherwise render "no children are ready to export" during the first fetch
  // or after a failed one.
  if (loading) return <LoadingChildren label="Loading your children…" />;
  if (error) return <ChildrenLoadError message={error} onRetry={refresh} />;

  return (
    <div>
      {/* Page header */}
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 28,
          color: "var(--ink-deep)",
          margin: "0 0 8px",
        }}
      >
        {t.title}
      </h1>
      <p
        style={{
          color: "var(--ink-soft)",
          fontSize: 15.5,
          margin: "0 0 28px",
          lineHeight: 1.6,
        }}
      >
        {t.subtitle}
      </p>

      {/* What's in an export */}
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          padding: "16px 20px",
          marginBottom: 28,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        {[t.bullet1, t.bullet2, t.bullet3].map((b) => (
          <p
            key={b}
            style={{ fontSize: 14.5, color: "var(--ink-soft)", margin: 0, lineHeight: 1.5 }}
          >
            {b}
          </p>
        ))}
      </div>

      {/* Child picker */}
      <SectionHeading>Pick a child</SectionHeading>
      {activeChildren.length === 0 ? (
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--line)",
            borderRadius: "var(--r-lg)",
            padding: "20px 22px",
            color: "var(--ink-soft)",
            fontSize: 15.5,
            marginBottom: 24,
          }}
        >
          No children with active access. Link a child first.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
          {activeChildren.map((lc) => (
            <ChildPickerCard
              key={lc.child.id}
              lc={lc}
              selected={selectedChildId === lc.child.id}
              ownerTagLabel={tch.ownerTag}
              viewerTagLabel={tch.viewerTag}
              onSelect={() => setSelectedChildId(lc.child.id)}
            />
          ))}
        </div>
      )}

      {/* Export panel — only shown once a child is selected */}
      {selectedLC &&
        (canExport && exportModel ? (
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--line)",
              borderRadius: "var(--r-lg)",
              padding: "22px 22px",
              display: "flex",
              flexDirection: "column",
              gap: 16,
              marginBottom: 20,
            }}
          >
            <h2
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: 16,
                color: "var(--ink-deep)",
                margin: 0,
              }}
            >
              Export for {selectedLC.child.displayLabel}
            </h2>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {/* PDF — generated imperatively on click (React 19-safe) */}
              <button
                onClick={() => void handleDownloadPdf()}
                disabled={pdfBusy}
                style={{
                  minHeight: 48,
                  padding: "0 22px",
                  border: 0,
                  background: "var(--brand)",
                  color: "#fff",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: 15,
                  borderRadius: "var(--r-pill)",
                  cursor: pdfBusy ? "wait" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                {pdfBusy ? t.preparing : t.generateButton}
              </button>
              {canShareFiles && (
                <button
                  onClick={() => void handleShare()}
                  disabled={pdfBusy}
                  style={{
                    minHeight: 48,
                    padding: "0 22px",
                    border: "1.5px solid var(--brand)",
                    background: "transparent",
                    color: "var(--brand)",
                    fontFamily: "var(--font-body)",
                    fontWeight: 600,
                    fontSize: 15,
                    borderRadius: "var(--r-pill)",
                    cursor: pdfBusy ? "wait" : "pointer",
                  }}
                >
                  {pdfBlob ? t.shareButton : t.preparing}
                </button>
              )}
              {pdfError && (
                <p
                  style={{
                    flexBasis: "100%",
                    margin: "4px 0 0",
                    fontSize: 13,
                    color: "var(--peach-deep)",
                  }}
                >
                  {pdfError}
                </p>
              )}

              {/* JSON */}
              <button
                onClick={handleDownloadJson}
                style={{
                  minHeight: 48,
                  padding: "0 20px",
                  border: "1.5px solid var(--brand)",
                  background: "transparent",
                  color: "var(--brand)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: 15,
                  borderRadius: "var(--r-pill)",
                  cursor: "pointer",
                }}
              >
                Download JSON
              </button>

              {/* CSV */}
              <button
                onClick={handleDownloadCsv}
                style={{
                  minHeight: 48,
                  padding: "0 20px",
                  border: "1.5px solid var(--line)",
                  background: "transparent",
                  color: "var(--ink-soft)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: 15,
                  borderRadius: "var(--r-pill)",
                  cursor: "pointer",
                }}
              >
                Download CSV
              </button>
            </div>

            {/* Guardrail note in the export panel */}
            <p
              style={{
                fontSize: 12.5,
                color: "var(--ink-faint)",
                margin: 0,
                lineHeight: 1.55,
                background: "var(--surface-sunk)",
                border: "1px solid var(--line)",
                borderRadius: "var(--r-lg)",
                padding: "10px 14px",
              }}
            >
              {GUARDRAIL_LINE}
            </p>
          </div>
        ) : (
          /* Consent gate — warm peach notice */
          <div
            style={{
              background: "var(--peach-tint)",
              border: "1px solid var(--peach)",
              borderRadius: "var(--r-lg)",
              padding: "20px 22px",
              marginBottom: 20,
            }}
          >
            <p
              style={{
                fontWeight: 700,
                fontSize: 15.5,
                color: "var(--peach-deep)",
                margin: "0 0 8px",
              }}
            >
              Export consent required
            </p>
            <p
              style={{
                fontSize: 14.5,
                color: "var(--peach-deep)",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              The owner of {selectedLC.child.displayLabel} hasn&apos;t granted export
              access yet. Ask them to enable &ldquo;Allow progress export&rdquo; in
              Sharing &amp; consent.
            </p>
          </div>
        ))}

      {/* Engagement guardrail — always present (matches other caregiver screens) */}
      <p style={{ fontSize: 13, color: "var(--ink-faint)", margin: 0, lineHeight: 1.55 }}>
        {th.engagementOnly}
      </p>
    </div>
  );
}
